import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Badge } from '../components/ui/badge'
import { Checkbox } from '../components/ui/checkbox'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs'
import { useToast } from '../hooks/use-toast'
import { ArrowLeft, Plus, RefreshCw, Trash2, TestTube, Zap, CheckCircle, XCircle, Clock, Settings } from 'lucide-react'
import { type ConnectorDefinition } from '../config/connectors'

interface Connector {
  id: string
  user_id: string
  name: string
  type: string
  provider: string
  is_active: boolean
  is_configured: boolean
  config: any
  last_sync_at: string | null
  last_sync_status: string | null
  sync_error: string | null
  total_synced: number
  created_at: string
  updated_at: string
}

interface ConnectorCreate {
  name: string
  type: string
  provider: string
  config: Record<string, any>
}

export default function ConnectorsPage() {
  const { logout } = useAuth()
  const navigate = useNavigate()
  const { toast } = useToast()
  
  const [connectors, setConnectors] = useState<Connector[]>([])
  const [loading, setLoading] = useState(true)
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isConfigDialogOpen, setIsConfigDialogOpen] = useState(false)
  const [, setSelectedDefinition] = useState<ConnectorDefinition | null>(null)
  const [selectedConnectorForConfig, setSelectedConnectorForConfig] = useState<Connector | null>(null)
  const [syncing, setSyncing] = useState<string | null>(null)
  const [testing, setTesting] = useState<string | null>(null)
  
  const [, setAvailableModules] = useState<{
    api_name: string
    module_label: string
  }[]>([])
  const [isLoadingModules, setIsLoadingModules] = useState(false)
  const [moduleFields, setModuleFields] = useState<Record<string, any[]>>({})
  
  const [syncConfig, setSyncConfig] = useState({
    modules: {} as Record<string, any>,
    sync_options: {
      limit: 100,
      date_range_days: 7,
    }
  })
  
  const [formData, setFormData] = useState<ConnectorCreate>({
    name: '',
    type: 'crm',
    provider: '',
    config: {},
  })

  useEffect(() => {
    loadConnectors()
  }, [])

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'crm',
      provider: '',
      config: {},
    })
    setSelectedDefinition(null)
  }

  const openConfigDialog = async (connector: Connector) => {
    console.log('Opening config dialog for:', connector)
    setSelectedConnectorForConfig(connector)
    setIsConfigDialogOpen(true)
    
    // Fetch available modules from Zoho
    setIsLoadingModules(true)
    try {
      const token = localStorage.getItem('access_token')
      const response = await fetch(
        `http://localhost:8000/api/v1/connectors/${connector.id}/modules`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      )

      if (response.ok) {
        const data = await response.json()
        console.log('Available modules:', data.modules)
        setAvailableModules(data.modules)
        
        // Initialize config with fetched modules
        const initialModules: Record<string, any> = {}
        data.modules.forEach((module: any) => {
          const apiName = module.api_name
          initialModules[apiName] = {
            enabled: false,
            module_label: module.plural_label || module.module_name,
            category: 'general',
            tags: [apiName.toLowerCase(), 'crm'],
          }
        })
        
        // Load existing config if available
        if (connector.config && connector.config.sync_settings) {
          console.log('Loading existing config:', connector.config.sync_settings)
          // Merge with existing config
          Object.keys(connector.config.sync_settings.modules || {}).forEach(key => {
            if (initialModules[key]) {
              initialModules[key] = {
                ...initialModules[key],
                ...connector.config.sync_settings.modules[key]
              }
            }
          })
          setSyncConfig({
            modules: initialModules,
            sync_options: connector.config.sync_settings.sync_options || {
              limit: 100,
              date_range_days: 7,
            }
          })
        } else {
          setSyncConfig({
            modules: initialModules,
            sync_options: {
              limit: 100,
              date_range_days: 7,
            }
          })
        }
      } else {
        toast({
          title: 'Error',
          description: 'Failed to fetch available modules',
          variant: 'destructive',
        })
      }
    } catch (error: any) {
      console.error('Error fetching modules:', error)
      toast({
        title: 'Error',
        description: 'Failed to fetch available modules',
        variant: 'destructive',
      })
    } finally {
      setIsLoadingModules(false)
    }
  }

  const updateModuleEnabled = async (moduleName: string, enabled: boolean) => {
    setSyncConfig(prev => ({
      ...prev,
      modules: {
        ...prev.modules,
        [moduleName]: {
          ...prev.modules[moduleName],
          enabled,
          fields: enabled ? (prev.modules[moduleName]?.fields || []) : [],
        }
      }
    }))
    
    // Fetch fields when module is enabled
    if (enabled && !moduleFields[moduleName] && selectedConnectorForConfig) {
      try {
        const token = localStorage.getItem('access_token')
        const response = await fetch(
          `http://localhost:8000/api/v1/connectors/${selectedConnectorForConfig.id}/modules/${moduleName}/fields`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          }
        )
        
        if (response.ok) {
          const data = await response.json()
          setModuleFields(prev => ({
            ...prev,
            [moduleName]: data.fields || []
          }))
          
          // Auto-select all fields by default
          setSyncConfig(prev => ({
            ...prev,
            modules: {
              ...prev.modules,
              [moduleName]: {
                ...prev.modules[moduleName],
                fields: data.fields?.map((f: any) => f.api_name) || [],
              }
            }
          }))
        }
      } catch (error) {
        console.error('Failed to fetch fields:', error)
      }
    }
  }

  const updateModuleField = (module: string, field: string, value: any) => {
    setSyncConfig({
      ...syncConfig,
      modules: {
        ...syncConfig.modules,
        [module]: {
          ...syncConfig.modules[module as keyof typeof syncConfig.modules],
          [field]: value
        }
      }
    })
  }

  const updateSyncOption = (option: string, value: any) => {
    setSyncConfig({
      ...syncConfig,
      sync_options: {
        ...syncConfig.sync_options,
        [option]: value
      }
    })
  }

  const saveConfiguration = async () => {
    if (!selectedConnectorForConfig) return

    try {
      const token = localStorage.getItem('access_token')
      
      const configToSave = {
        config: {
          ...selectedConnectorForConfig.config,
          sync_settings: syncConfig
        }
      }
      
      console.log('Saving config:', configToSave)
      
      const response = await fetch(
        `http://localhost:8000/api/v1/connectors/${selectedConnectorForConfig.id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(configToSave),
        }
      )

      if (!response.ok) {
        const errorData = await response.json()
        console.error('Save failed:', errorData)
        throw new Error(errorData.detail || 'Failed to save configuration')
      }

      toast({
        title: 'Success',
        description: 'Sync configuration saved',
      })

      setIsConfigDialogOpen(false)
      loadConnectors()
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    }
  }

  // Unused function - kept for future use
  // const handleSelectConnector = (definition: ConnectorDefinition) => {
  //   setSelectedDefinition(definition)
  //   const initialConfig: Record<string, any> = {}
  //   definition.fields.forEach(field => {
  //     if (field.type === 'select' && field.options && field.options.length > 0) {
  //       initialConfig[field.name] = field.options[0].value
  //     } else {
  //       initialConfig[field.name] = ''
  //     }
  //   })
  //   setFormData({
  //     name: definition.name,
  //     type: definition.type,
  //     provider: definition.provider,
  //     config: initialConfig,
  //   })
  // }

  // Unused function - kept for future use
  // const updateConfigField = (fieldName: string, value: string) => {
  //   setFormData({
  //     ...formData,
  //     config: {
  //       ...formData.config,
  //       [fieldName]: value,
  //     },
  //   })
  // }

  const loadConnectors = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('access_token')
      const response = await fetch('http://localhost:8000/api/v1/connectors', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.status === 401) {
        logout()
        return
      }

      if (!response.ok) {
        throw new Error('Failed to load connectors')
      }

      const data = await response.json()
      setConnectors(data)
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to load connectors',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleCreateConnector = async () => {
    try {
      const token = localStorage.getItem('access_token')
      const response = await fetch('http://localhost:8000/api/v1/connectors/', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.detail || 'Failed to create connector')
      }

      toast({
        title: 'Success',
        description: 'Connector created successfully',
      })

      setIsCreateDialogOpen(false)
      resetForm()
      loadConnectors()
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create connector',
        variant: 'destructive',
      })
    }
  }

  const handleTestConnection = async (connectorId: string) => {
    try {
      setTesting(connectorId)
      const token = localStorage.getItem('access_token')
      const response = await fetch(`http://localhost:8000/api/v1/connectors/${connectorId}/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error('Connection test failed')
      }

      const result = await response.json()

      if (result.connected) {
        toast({
          title: 'Success',
          description: 'Connection test successful!',
        })
        loadConnectors()
      } else {
        toast({
          title: 'Failed',
          description: result.message || 'Connection test failed',
          variant: 'destructive',
        })
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Connection test failed',
        variant: 'destructive',
      })
    } finally {
      setTesting(null)
    }
  }

  const handleSync = async (connectorId: string) => {
    try {
      setSyncing(connectorId)
      const token = localStorage.getItem('access_token')
      const response = await fetch(`http://localhost:8000/api/v1/connectors/${connectorId}/sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          resource_types: ['Leads', 'Contacts', 'Deals'],
          limit: 100,
        }),
      })

      if (!response.ok) {
        throw new Error('Sync failed')
      }

      const result = await response.json()

      toast({
        title: 'Success',
        description: result.message || 'Sync completed successfully',
      })

      loadConnectors()
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Sync failed',
        variant: 'destructive',
      })
    } finally {
      setSyncing(null)
    }
  }

  const handleDelete = async (connectorId: string) => {
    if (!confirm('Are you sure you want to delete this connector?')) {
      return
    }

    try {
      const token = localStorage.getItem('access_token')
      const response = await fetch(`http://localhost:8000/api/v1/connectors/${connectorId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error('Failed to delete connector')
      }

      toast({
        title: 'Success',
        description: 'Connector deleted successfully',
      })

      loadConnectors()
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete connector',
        variant: 'destructive',
      })
    }
  }

  const getStatusBadge = (connector: Connector) => {
    if (!connector.is_active) {
      return <Badge variant="secondary">Inactive</Badge>
    }
    if (connector.last_sync_status === 'success') {
      return <Badge variant="default" className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Active</Badge>
    }
    if (connector.last_sync_status === 'failed') {
      return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Error</Badge>
    }
    return <Badge variant="outline"><Clock className="w-3 h-3 mr-1" />Pending</Badge>
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Connectors</h1>
                <p className="text-sm text-gray-500">Connect external data sources</p>
              </div>
            </div>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              New Connector
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : connectors.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {connectors.map((connector) => (
              <Card key={connector.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg">{connector.name}</CardTitle>
                      <CardDescription className="mt-1">
                        {connector.provider.charAt(0).toUpperCase() + connector.provider.slice(1)} {connector.type.toUpperCase()}
                      </CardDescription>
                    </div>
                    {getStatusBadge(connector)}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Total Synced</p>
                        <p className="font-semibold">{connector.total_synced}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Last Sync</p>
                        <p className="font-semibold">
                          {connector.last_sync_at
                            ? new Date(connector.last_sync_at).toLocaleDateString()
                            : 'Never'}
                        </p>
                      </div>
                    </div>

                    {/* Error Message */}
                    {connector.sync_error && (
                      <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800">
                        {connector.sync_error}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleTestConnection(connector.id)}
                        disabled={testing === connector.id}
                      >
                        <TestTube className="w-4 h-4 mr-1" />
                        {testing === connector.id ? 'Testing...' : 'Test'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openConfigDialog(connector)}
                      >
                        <Settings className="w-4 h-4 mr-1" />
                        Configure
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleSync(connector.id)}
                        disabled={!connector.is_active || syncing === connector.id}
                      >
                        <RefreshCw className={`w-4 h-4 mr-1 ${syncing === connector.id ? 'animate-spin' : ''}`} />
                        {syncing === connector.id ? 'Syncing...' : 'Sync'}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(connector.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="flex flex-col items-center justify-center p-12">
              <Zap className="w-16 h-16 text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold mb-2">No connectors yet</h3>
              <p className="text-gray-500 mb-4 text-center">
                Connect external data sources like Zoho CRM to automatically import data
              </p>
              <Button onClick={() => setIsCreateDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Connector
              </Button>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Create Connector Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Connector</DialogTitle>
            <DialogDescription>
              Connect an external data source to automatically import data
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Connector Name</Label>
              <Input
                id="name"
                placeholder="My Zoho CRM"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="type">Type</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) => setFormData({ ...formData, type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="crm">CRM</SelectItem>
                    <SelectItem value="ads">Ads</SelectItem>
                    <SelectItem value="social">Social Media</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="provider">Provider</Label>
                <Select
                  value={formData.provider}
                  onValueChange={(value) => setFormData({ ...formData, provider: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="zoho">Zoho CRM</SelectItem>
                    <SelectItem value="salesforce" disabled>Salesforce (Coming Soon)</SelectItem>
                    <SelectItem value="hubspot" disabled>HubSpot (Coming Soon)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {formData.provider === 'zoho' && (
              <>
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-semibold text-sm mb-2">Setup Instructions:</h4>
                  <ol className="text-sm space-y-1 list-decimal list-inside text-blue-900">
                    <li>Go to <a href="https://api-console.zoho.com/" target="_blank" rel="noopener noreferrer" className="underline">Zoho API Console</a></li>
                    <li>Create a Server-based Application</li>
                    <li>Copy Client ID and Client Secret</li>
                    <li>Generate Refresh Token (see docs)</li>
                  </ol>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="client_id">Client ID</Label>
                  <Input
                    id="client_id"
                    placeholder="1000.XXXXXXXXXXXXX"
                    value={formData.config.client_id}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        config: { ...formData.config, client_id: e.target.value },
                      })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="client_secret">Client Secret</Label>
                  <Input
                    id="client_secret"
                    type="password"
                    placeholder="••••••••••••••••"
                    value={formData.config.client_secret}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        config: { ...formData.config, client_secret: e.target.value },
                      })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="refresh_token">Refresh Token</Label>
                  <Input
                    id="refresh_token"
                    type="password"
                    placeholder="1000.xxxxxxxxxxxxx.yyyyyyyyyyyy"
                    value={formData.config.refresh_token}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        config: { ...formData.config, refresh_token: e.target.value },
                      })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="data_center">Data Center</Label>
                  <Select
                    value={formData.config.data_center}
                    onValueChange={(value) =>
                      setFormData({
                        ...formData,
                        config: { ...formData.config, data_center: value },
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="com">US (.com)</SelectItem>
                      <SelectItem value="eu">Europe (.eu)</SelectItem>
                      <SelectItem value="in">India (.in)</SelectItem>
                      <SelectItem value="au">Australia (.com.au)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateConnector}>Create Connector</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sync Configuration Dialog */}
      <Dialog open={isConfigDialogOpen} onOpenChange={setIsConfigDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Configure Sync Settings</DialogTitle>
            <DialogDescription>
              Choose which modules to sync and configure field mappings
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="modules" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="modules">Modules</TabsTrigger>
              <TabsTrigger value="options">Sync Options</TabsTrigger>
            </TabsList>

            {/* Modules Tab */}
            <TabsContent value="modules" className="space-y-4 mt-4">
              {isLoadingModules ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  <p className="mt-2 text-sm text-gray-500">Loading modules from Zoho CRM...</p>
                </div>
              ) : Object.keys(syncConfig.modules).length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No modules available</p>
                </div>
              ) : (
                Object.entries(syncConfig.modules).map(([moduleName, moduleConfig]) => (
                  <Card key={moduleName}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={moduleConfig.enabled}
                            onCheckedChange={(checked) => 
                              updateModuleEnabled(moduleName, checked as boolean)
                            }
                          />
                          <CardTitle className="text-base">
                            {moduleConfig.module_label || moduleName}
                          </CardTitle>
                        </div>
                        <Badge variant="secondary">Zoho CRM</Badge>
                      </div>
                    </CardHeader>
                  
                  {moduleConfig.enabled && (
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Category</Label>
                          <Select
                            value={moduleConfig.category}
                            onValueChange={(value) => 
                              updateModuleField(moduleName, 'category', value)
                            }
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="sales">Sales</SelectItem>
                              <SelectItem value="marketing">Marketing</SelectItem>
                              <SelectItem value="crm">CRM</SelectItem>
                              <SelectItem value="business">Business</SelectItem>
                              <SelectItem value="task">Task</SelectItem>
                              <SelectItem value="note">Note</SelectItem>
                              <SelectItem value="general">General</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label>Tags (comma-separated)</Label>
                          <Input
                            value={moduleConfig.tags.join(', ')}
                            onChange={(e) => 
                              updateModuleField(moduleName, 'tags', e.target.value.split(',').map(t => t.trim()))
                            }
                            placeholder="e.g., lead, crm, sales"
                          />
                        </div>
                      </div>
                      
                      {/* Field Selection */}
                      {moduleFields[moduleName] && moduleFields[moduleName].length > 0 && (
                        <div className="space-y-2 pt-4 border-t">
                          <Label className="text-sm font-semibold">Select Fields to Sync</Label>
                          <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 border rounded">
                            {moduleFields[moduleName].map((field: any) => (
                              <div key={field.api_name} className="flex items-center space-x-2">
                                <Checkbox
                                  id={`${moduleName}-${field.api_name}`}
                                  checked={moduleConfig.fields?.includes(field.api_name) || false}
                                  onCheckedChange={(checked) => {
                                    const currentFields = moduleConfig.fields || []
                                    const newFields = checked
                                      ? [...currentFields, field.api_name]
                                      : currentFields.filter((f: string) => f !== field.api_name)
                                    updateModuleField(moduleName, 'fields', newFields)
                                  }}
                                />
                                <label
                                  htmlFor={`${moduleName}-${field.api_name}`}
                                  className="text-sm cursor-pointer"
                                >
                                  {field.field_label || field.api_name}
                                </label>
                              </div>
                            ))}
                          </div>
                          <div className="flex justify-between text-xs text-muted-foreground">
                            <span>{moduleConfig.fields?.length || 0} of {moduleFields[moduleName].length} fields selected</span>
                            <div className="space-x-2">
                              <button
                                onClick={() => updateModuleField(moduleName, 'fields', moduleFields[moduleName].map((f: any) => f.api_name))}
                                className="text-primary hover:underline"
                              >
                                Select All
                              </button>
                              <button
                                onClick={() => updateModuleField(moduleName, 'fields', [])}
                                className="text-primary hover:underline"
                              >
                                Clear All
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  )}
                </Card>
                ))
              )}
            </TabsContent>

            {/* Options Tab */}
            <TabsContent value="options" className="space-y-4 mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Sync Limits</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Max Records per Sync</Label>
                    <Select
                      value={syncConfig.sync_options.limit.toString()}
                      onValueChange={(value) => 
                        updateSyncOption('limit', parseInt(value))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="50">50 records</SelectItem>
                        <SelectItem value="100">100 records</SelectItem>
                        <SelectItem value="200">200 records</SelectItem>
                        <SelectItem value="500">500 records</SelectItem>
                        <SelectItem value="1000">1000 records</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Date Range</Label>
                    <Select
                      value={syncConfig.sync_options.date_range_days.toString()}
                      onValueChange={(value) => 
                        updateSyncOption('date_range_days', parseInt(value))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Last 24 hours</SelectItem>
                        <SelectItem value="7">Last 7 days</SelectItem>
                        <SelectItem value="30">Last 30 days</SelectItem>
                        <SelectItem value="90">Last 90 days</SelectItem>
                        <SelectItem value="0">All time</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsConfigDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveConfiguration}>
              Save Configuration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
